import React, { useEffect } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import {
  getHatchCollectionProducts,
  getHatchCollectionFetchingState,
} from "../../utils/reduxSelectors";
import { fetchCollectionRequest } from "../../actions/ShopActions";
import { getProductData } from "../../utils/shop";
import { usePrevious } from "../../utils/hooks";

// Constants
import {
  HATCH_PRODUCT_HANDLES,
  PRODUCT_KEYS,
} from "../../utils/productConstants";
import {
  COLLECTION_HANDLES,
  HATCH_COLLECTION_KEY,
} from "../../utils/collectionConstants";

let hasFetched = false;

/*
  Custom hook to pull in Hatch product collection data from Shopify.
  Use this in functional components.
  Reference:
  https://blog.logrocket.com/how-to-convert-your-existing-redux-containers-to-hooks/
*/
export const useHatchProductCollection = (productKeys = null) => {
  const hatchCollectionData = useSelector(
    (state) => getHatchCollectionProducts(state),
    shallowEqual
  );
  const isFetching = useSelector((state) =>
    getHatchCollectionFetchingState(state)
  );
  const prevIsFetchingState = usePrevious(isFetching, false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isFetching && !hasFetched) {
      dispatch(
        fetchCollectionRequest(COLLECTION_HANDLES[HATCH_COLLECTION_KEY])
      );
      hasFetched = true;
    }
  }, [dispatch, isFetching]);

  // If productKeys is supplied, just return the targeted products
  if (productKeys) {
    // If an array of keys is passed in, return only those products
    // as a map, keyed by the product key for easy access
    if (Array.isArray(productKeys)) {
      return productKeys.reduce((productMap, productKey) => {
        productMap[productKey] = {
          data: hatchCollectionData[productKey] || {},
          isFetching,
          fetchComplete: !isFetching && !!prevIsFetchingState,
        };

        return productMap;
      }, {});
    }

    return {
      data: hatchCollectionData[productKeys],
      isFetching,
      fetchComplete: !isFetching && !!prevIsFetchingState,
    };
  }

  return {
    [PRODUCT_KEYS.REST_KEY]: getProductData(
      hatchCollectionData,
      HATCH_PRODUCT_HANDLES[PRODUCT_KEYS.REST_KEY]
    ),
    [PRODUCT_KEYS.NEW_REST_KEY]: getProductData(
      hatchCollectionData,
      HATCH_PRODUCT_HANDLES[PRODUCT_KEYS.NEW_REST_KEY]
    ),
    [PRODUCT_KEYS.REST_PLUS_KEY]: getProductData(
      hatchCollectionData,
      HATCH_PRODUCT_HANDLES[PRODUCT_KEYS.REST_PLUS_KEY]
    ),
    [PRODUCT_KEYS.COVERLETS_KEY]: getProductData(
      hatchCollectionData,
      HATCH_PRODUCT_HANDLES[PRODUCT_KEYS.COVERLETS_KEY]
    ),
    [PRODUCT_KEYS.MILESTONE_CARD_KEY]: getProductData(
      hatchCollectionData,
      HATCH_PRODUCT_HANDLES[PRODUCT_KEYS.MILESTONE_CARD_KEY]
    ),
    [PRODUCT_KEYS.GROW_KEY]: getProductData(
      hatchCollectionData,
      HATCH_PRODUCT_HANDLES[PRODUCT_KEYS.GROW_KEY]
    ),
    [PRODUCT_KEYS.REST_TRAVEL_CASE_KEY]: getProductData(
      hatchCollectionData,
      HATCH_PRODUCT_HANDLES[PRODUCT_KEYS.REST_TRAVEL_CASE_KEY]
    ),
    [PRODUCT_KEYS.RESTORE_KEY]: getProductData(
      hatchCollectionData,
      HATCH_PRODUCT_HANDLES[PRODUCT_KEYS.RESTORE_KEY]
    ),
    [PRODUCT_KEYS.RESTORE_LINKEDIN_KEY]: getProductData(
      hatchCollectionData,
      HATCH_PRODUCT_HANDLES[PRODUCT_KEYS.RESTORE_LINKEDIN_KEY]
    ),
    [PRODUCT_KEYS.REST_MINI_KEY]: getProductData(
      hatchCollectionData,
      HATCH_PRODUCT_HANDLES[PRODUCT_KEYS.REST_MINI_KEY]
    ),
    [PRODUCT_KEYS.REST_REST_PLUS_BARREL_POWER_CORD_KEY]: getProductData(
      hatchCollectionData,
      HATCH_PRODUCT_HANDLES[PRODUCT_KEYS.REST_REST_PLUS_BARREL_POWER_CORD_KEY]
    ),
    [PRODUCT_KEYS.RESTORE_BARREL_POWER_CORD_KEY]: getProductData(
      hatchCollectionData,
      HATCH_PRODUCT_HANDLES[PRODUCT_KEYS.RESTORE_BARREL_POWER_CORD_KEY]
    ),
    [PRODUCT_KEYS.REST_REST_PLUS_USB_POWER_CORD_KEY]: getProductData(
      hatchCollectionData,
      HATCH_PRODUCT_HANDLES[PRODUCT_KEYS.REST_REST_PLUS_USB_POWER_CORD_KEY]
    ),
    [PRODUCT_KEYS.HATCH_CLOUD_PILLOW_KEY]: getProductData(
      hatchCollectionData,
      HATCH_PRODUCT_HANDLES[PRODUCT_KEYS.HATCH_CLOUD_PILLOW_KEY]
    ),
  };
};

/*
  Container/wrapper component that uses custom hook to pull in Hatch product
  collection data from Shopify.
  Use this in class components.
*/
const withHatchProductCollection = (WrappedComponent) => {
  return (props) => {
    const hatchCollectionData = useHatchProductCollection();
    return <WrappedComponent productData={hatchCollectionData} {...props} />;
  };
};

export default withHatchProductCollection;
